import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Phone, Mail, Plus, Edit2, Trash2, Search, Users, 
  Building2, Wrench, Stethoscope, Laptop, Truck, Zap, Droplets,
  Grid3X3, Car, Settings, Bike, Egg, CircleUser, ShoppingCart, MoreHorizontal
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const CATEGORIES = [
  { value: 'management', label: 'Management', icon: Building2, color: 'text-blue-600 bg-blue-100' },
  { value: 'staff', label: 'Staff', icon: Users, color: 'text-emerald-600 bg-emerald-100' },
  { value: 'it', label: 'IT', icon: Laptop, color: 'text-purple-600 bg-purple-100' },
  { value: 'distributor', label: 'Distributor', icon: Truck, color: 'text-amber-600 bg-amber-100' },
  { value: 'contractor', label: 'Contractor', icon: Wrench, color: 'text-slate-600 bg-slate-100' },
  { value: 'consultant', label: 'Consultant', icon: Stethoscope, color: 'text-pink-600 bg-pink-100' },
  { value: 'electrical_repairs', label: 'Electrical Repairs', icon: Zap, color: 'text-yellow-600 bg-yellow-100' },
  { value: 'plumbing', label: 'Plumbing', icon: Droplets, color: 'text-cyan-600 bg-cyan-100' },
  { value: 'cage_repairs', label: 'Cage Repairs', icon: Grid3X3, color: 'text-orange-600 bg-orange-100' },
  { value: 'auto_electrician', label: 'Auto Electrician', icon: Car, color: 'text-indigo-600 bg-indigo-100' },
  { value: 'auto_mechanic', label: 'Auto Mechanic', icon: Settings, color: 'text-red-600 bg-red-100' },
  { value: 'okada_operator', label: 'Okada Operator', icon: Bike, color: 'text-lime-600 bg-lime-100' },
  { value: 'hatchery', label: 'Hatchery', icon: Egg, color: 'text-rose-600 bg-rose-100' },
  { value: 'driver', label: 'Driver', icon: CircleUser, color: 'text-teal-600 bg-teal-100' },
  { value: 'customer', label: 'Customer', icon: ShoppingCart, color: 'text-violet-600 bg-violet-100' },
  { value: 'feed_supplier', label: 'Feed Supplier', icon: Truck, color: 'text-amber-700 bg-amber-50' },
  { value: 'miscellaneous', label: 'Miscellaneous', icon: MoreHorizontal, color: 'text-gray-600 bg-gray-100' },
];

const Directory = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    category: 'staff',
    name: '',
    role: '',
    phone: '',
    phone2: '',
    email: '',
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
    // Seed data if empty
    seedDataIfEmpty();
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/directory`, getAuthHeader());
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch directory');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const seedDataIfEmpty = async () => {
    try {
      await axios.post(`${API}/directory/seed`, {}, getAuthHeader());
      fetchEntries();
    } catch (error) {
      // Ignore errors - data might already exist
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      if (editingEntry) {
        await axios.put(`${API}/directory/${editingEntry.id}`, formData, getAuthHeader());
        toast.success('Entry updated successfully');
      } else {
        await axios.post(`${API}/directory`, formData, getAuthHeader());
        toast.success('Entry added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      name: entry.name,
      role: entry.role || '',
      phone: entry.phone || '',
      phone2: entry.phone2 || '',
      email: entry.email || '',
      notes: entry.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      await axios.delete(`${API}/directory/${entryId}`, getAuthHeader());
      toast.success('Entry deleted successfully');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      category: 'staff',
      name: '',
      role: '',
      phone: '',
      phone2: '',
      email: '',
      notes: ''
    });
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = 
      entry.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.phone?.includes(searchTerm) ||
      entry.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[1];
  };

  const getEntriesByCategory = (category) => {
    return filteredEntries.filter(e => e.category === category);
  };

  const renderEntryCard = (entry) => {
    const categoryInfo = getCategoryInfo(entry.category);
    const Icon = categoryInfo.icon;
    
    return (
      <div 
        key={entry.id} 
        className="card p-4 hover:shadow-md transition-shadow"
        data-testid={`directory-entry-${entry.id}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{entry.name}</h3>
              {entry.role && (
                <p className="text-sm text-slate-600">{entry.role}</p>
              )}
              
              <div className="mt-2 space-y-1">
                {entry.phone && (
                  <a 
                    href={`tel:${entry.phone.replace(/[^0-9+]/g, '')}`}
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    <Phone className="w-4 h-4" />
                    {entry.phone}
                  </a>
                )}
                {entry.phone2 && (
                  <a 
                    href={`tel:${entry.phone2.replace(/[^0-9+]/g, '')}`}
                    className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    <Phone className="w-4 h-4" />
                    {entry.phone2}
                  </a>
                )}
                {entry.email && (
                  <a 
                    href={`mailto:${entry.email}`}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Mail className="w-4 h-4" />
                    {entry.email}
                  </a>
                )}
              </div>
              
              {entry.notes && (
                <p className="mt-2 text-xs text-slate-500">{entry.notes}</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-1">
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleEdit(entry)}
              title="Edit"
            >
              <Edit2 className="w-4 h-4 text-blue-600" />
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleDelete(entry.id)}
              title="Delete"
            >
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="directory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Directory
          </h1>
          <p className="text-slate-600 mt-1">
            Telephone and email directory
          </p>
        </div>
        
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-entry-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Entry
        </Button>
      </div>

      {/* Search and Category Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={selectedCategory === 'all' 
              ? "Search by name, role, phone, or email..." 
              : `Search within ${CATEGORIES.find(c => c.value === selectedCategory)?.label || 'category'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <div className="w-full sm:w-64">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger data-testid="category-filter">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  All Categories ({entries.length})
                </div>
              </SelectItem>
              {CATEGORIES.map(category => {
                const count = entries.filter(e => e.category === category.value).length;
                const Icon = category.icon;
                return (
                  <SelectItem key={category.value} value={category.value}>
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {category.label} ({count})
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Quick Filters - Scrollable */}
      <div className="overflow-x-auto pb-2">
        <div className="flex gap-2 min-w-max">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
              selectedCategory === 'all'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="filter-all"
          >
            <Users className="w-3.5 h-3.5" />
            All
          </button>
          {CATEGORIES.map(category => {
            const count = entries.filter(e => e.category === category.value).length;
            const Icon = category.icon;
            return (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  selectedCategory === category.value
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${category.value}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {category.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Directory Entries */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading directory...</div>
        ) : filteredEntries.length === 0 ? (
          <div className="card p-12 text-center">
            <Phone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No Entries Found</h3>
            <p className="text-slate-500 mt-2">
              {searchTerm ? 'Try a different search term.' : 'Add your first directory entry using the button above.'}
            </p>
          </div>
        ) : selectedCategory === 'all' ? (
          // Show grouped by category when "All" is selected
          CATEGORIES.map(category => {
            const categoryEntries = getEntriesByCategory(category.value);
            if (categoryEntries.length === 0) return null;
            
            return (
              <div key={category.value}>
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <category.icon className={`w-5 h-5 ${category.color.split(' ')[0]}`} />
                  {category.label}
                  <span className="text-sm font-normal text-slate-500">({categoryEntries.length})</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryEntries.map(renderEntryCard)}
                </div>
              </div>
            );
          })
        ) : (
          // Show flat list when specific category is selected
          <div>
            {(() => {
              const categoryInfo = getCategoryInfo(selectedCategory);
              return (
                <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <categoryInfo.icon className={`w-5 h-5 ${categoryInfo.color.split(' ')[0]}`} />
                  {categoryInfo.label}
                  <span className="text-sm font-normal text-slate-500">({filteredEntries.length})</span>
                </h2>
              );
            })()}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEntries.map(renderEntryCard)}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md" data-testid="directory-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-emerald-600" />
              {editingEntry ? 'Edit Directory Entry' : 'Add Directory Entry'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(category => (
                    <SelectItem key={category.value} value={category.value}>
                      <div className="flex items-center gap-2">
                        <category.icon className="w-4 h-4" />
                        {category.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                data-testid="name-input"
                required
              />
            </div>
            
            <div>
              <Label>Role / Position</Label>
              <Input
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="e.g., Farm Manager, Supervisor"
                data-testid="role-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Primary phone"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label>Phone 2 (Optional)</Label>
                <Input
                  value={formData.phone2}
                  onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                  placeholder="Secondary phone"
                  data-testid="phone2-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes"
                data-testid="notes-input"
              />
            </div>
            
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-button">
                {editingEntry ? 'Update Entry' : 'Add Entry'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Directory;
